import { ImagemDaSecao } from '@/components/imagem-da-secao';
import { TituloComDestaque } from '@/components/titulo-com-destaque';
import { hrefDoEmail, hrefDoTelefone } from '@/content/contato';
import { useContato, useShowroom } from '@/hooks/use-conteudo';
import { useRevelarAoRolar } from '@/hooks/use-revelar-ao-rolar';

function revelacao(visivel: boolean): string {
  return `transition-[opacity,transform] duration-[900ms] ease-plane motion-reduce:transition-none ${
    visivel ? 'translate-y-0 opacity-100' : 'translate-y-[26px] opacity-0'
  }`;
}

/**
 * Recorte do mapa em volta do ponto, como no protótipo.
 *
 * A janela é retangular — o dobro de longitude que de latitude — porque o
 * quadro é 16:10. Com o mesmo raio nos dois eixos o mapa sai esticado.
 */
const RAIO_LON = 0.02;
const RAIO_LAT = 0.01;

/** Casas decimais do recorte. Sem arredondar, a soma vira -53.435199999999995. */
const CASAS = 6;

function enderecoDoMapa({ lat, lon }: { lat: number; lon: number }): string {
  const caixa = [lon - RAIO_LON, lat - RAIO_LAT, lon + RAIO_LON, lat + RAIO_LAT].map((grau) =>
    Number(grau.toFixed(CASAS)),
  );

  // OpenStreetMap e não Google Maps: o embed do Google grava cookie de rastreio
  // em quem só abriu a página. O protótipo já tinha feito essa escolha.
  return `https://www.openstreetmap.org/export/embed.html?bbox=${caixa.join('%2C')}&layer=mapnik&marker=${lat}%2C${lon}`;
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <dt className="mb-1.5 text-[0.64rem] tracking-[0.2em] text-brand-600 uppercase">{children}</dt>
  );
}

function Valor({ children }: { children: React.ReactNode }) {
  return <dd className="m-0 text-[0.94rem] leading-[1.55] text-ink">{children}</dd>;
}

const LINK_INFO =
  'border-b border-clay transition-colors duration-300 hover:border-brand-600 hover:text-brand-600';

/**
 * Seção 05 — Onde a gente fica.
 *
 * Portada de `legacy/static-site/index.html`, seção `#showroom`.
 *
 * O mapa e o botão de rota só são montados quando `CONTATO.coordenadas` deixa
 * de ser `null`. As coordenadas do protótipo são de demonstração, e o
 * `LEIA-ME.md` manda trocá-las: um mapa apontando para o lugar errado não é um
 * detalhe visual, é alguém dirigindo até o endereço errado. Sem elas fica o
 * quadro com a etiqueta, e o endereço em texto continua na lista ao lado.
 *
 * A foto do showroom segue a mesma regra das outras — ver `atelie-content.ts`.
 */
export function ShowroomSection() {
  const { ref, visivel } = useRevelarAoRolar<HTMLElement>();
  const contato = useContato();
  const showroom = useShowroom();
  const { coordenadas } = contato;

  return (
    <section
      ref={ref}
      id="showroom"
      className="bg-bone-soft py-[clamp(70px,10vw,140px)]"
      aria-labelledby="showroom-titulo"
    >
      <div className="mx-auto grid w-full max-w-[1360px] grid-cols-6 items-start gap-x-[clamp(16px,2.4vw,32px)] gap-y-[clamp(24px,3vw,44px)] px-6 min-[980px]:grid-cols-12">
        <div className={`col-[1/7] min-[980px]:col-[1/4] ${revelacao(visivel)}`}>
          <p className="flex items-center gap-2.5 text-[0.72rem] font-medium tracking-[0.22em] text-brand-600 uppercase">
            <span
              aria-hidden="true"
              className="inline-block h-px w-[34px] bg-brand-600 opacity-75"
            />
            {showroom.kicker}
          </p>
        </div>

        <div className={`col-[1/7] min-[980px]:col-[4/9] ${revelacao(visivel)}`}>
          <TituloComDestaque
            id="showroom-titulo"
            texto={showroom.titulo}
            className="mb-[0.5em] font-display text-[clamp(2.1rem,5.4vw,4.4rem)] leading-[0.99] tracking-[-0.025em] text-brand-950"
            destaqueClassName="font-light text-brand-600 italic"
          />

          <p className="m-0 text-[clamp(1.02rem,0.5vw+0.92rem,1.2rem)] leading-[1.6] text-ink-soft">
            {showroom.lead}
          </p>
        </div>

        <div
          className={`col-[1/7] min-[980px]:col-[9/13] min-[980px]:self-end ${revelacao(visivel)}`}
        >
          <dl className="mb-[26px] grid gap-5">
            <div>
              <Rotulo>Endereço</Rotulo>
              <Valor>
                {contato.enderecoLinha1}
                <br />
                {contato.enderecoLinha2}
              </Valor>
            </div>

            <div>
              <Rotulo>Atendimento</Rotulo>
              <Valor>
                {contato.horarioSemana}
                <br />
                {contato.horarioSabado}
              </Valor>
            </div>

            <div>
              <Rotulo>Falar com a gente</Rotulo>
              <Valor>
                <a href={hrefDoTelefone(contato)} className={LINK_INFO}>
                  {contato.telefoneExibicao}
                </a>
                <br />
                <a href={hrefDoEmail(contato)} className={LINK_INFO}>
                  {contato.email}
                </a>
              </Valor>
            </div>
          </dl>

          {coordenadas && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${coordenadas.lat},${coordenadas.lon}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative isolate inline-flex items-center gap-[0.7em] overflow-hidden border border-clay px-[1.6em] py-[1.05em] text-[0.82rem] font-medium tracking-[0.06em] text-brand-950 uppercase transition-colors duration-[350ms] ease-plane hover:border-brand-600 hover:text-bone active:translate-y-px"
            >
              {/* O preenchimento sobe de baixo, como o `::before` do protótipo. */}
              <span
                aria-hidden="true"
                className="absolute inset-0 -z-10 translate-y-full bg-brand-600 transition-transform duration-[450ms] ease-plane-slow group-hover:translate-y-0 motion-reduce:transition-none"
              />
              Traçar rota
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="h-[1.15em] w-[1.15em] transition-transform duration-[350ms] ease-plane group-hover:translate-x-1 motion-reduce:transition-none"
              >
                <path
                  d="M4 12h15m-6-6 6 6-6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          )}
        </div>

        <div
          className={`relative col-[1/7] aspect-[16/11] overflow-hidden bg-sand min-[980px]:col-[1/8] min-[980px]:aspect-[16/10] ${revelacao(visivel)}`}
        >
          {coordenadas && (
            <iframe
              title="Mapa do showroom da Plane Móveis"
              loading="lazy"
              /* Mais restrito que o protótipo, que mandava a URL inteira: a
                 OpenStreetMap não precisa saber de que página veio a visita. */
              referrerPolicy="no-referrer"
              src={enderecoDoMapa(coordenadas)}
              className="h-full w-full border-0 [filter:grayscale(1)_contrast(0.94)_sepia(0.16)]"
            />
          )}

          {showroom.etiquetaDoMapa !== '' && (
            <span className="absolute bottom-4 left-4 bg-brand-950 px-[1em] py-[0.6em] text-[0.64rem] tracking-[0.16em] text-bone uppercase">
              {showroom.etiquetaDoMapa}
            </span>
          )}
        </div>

        <ImagemDaSecao
          mediaId={showroom.foto}
          alt=""
          proporcao="aspect-[16/10]"
          className={`col-[1/7] min-[980px]:col-[8/13] ${revelacao(visivel)}`}
        />
      </div>
    </section>
  );
}
